// src/pages/Verify.jsx
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { db } from "../firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { useTranslation } from "react-i18next";
import QRCode from "react-qr-code";

/**
 * 把 Firestore 里的 expiresAt 转成 JS Date
 * 兼容:
 *  - Timestamp 对象 (有 toDate)
 *  - { seconds: xxx } 结构
 *  - 普通日期字符串 / 时间戳
 */
function getExpiresDate(expiresAt) {
  if (!expiresAt) {
    return null;
  }

  try {
    // Timestamp
    if (
      typeof expiresAt.toDate === "function"
    ) {
      return expiresAt.toDate();
    }

    // { seconds: 123456 }
    if (typeof expiresAt.seconds === "number") {
      return new Date(expiresAt.seconds * 1000);
    }

    // 兜底：字符串、毫秒时间戳等
    return new Date(expiresAt);
  } catch (e) {
    return null;
  }
}

/**
 * 统一计算会员状态文案：已开通 / 已过期 / 已停用
 */
function getMembershipStatusText(memberData, tDashboard) {
  var inactiveText =
    tDashboard("membershipInactive") || "已停用";
  var activeText =
    tDashboard("membershipActive") || "已开通";
  var expiredText =
    tDashboard("membershipExpired") || "已过期";

  if (!memberData) {
    return inactiveText;
  }

  // active 可能不存在，默认 true
  var isActive =
    typeof memberData.active === "boolean"
      ? memberData.active
      : true;

  var expiresDate = getExpiresDate(memberData.expiresAt);
  var isExpired = false;
  if (expiresDate) {
    isExpired = expiresDate.getTime() < Date.now();
  }

  if (!isActive) {
    return inactiveText;
  }

  if (isExpired) {
    return expiredText;
  }

  return activeText;
}

export default function Verify() {
  const { t: tDashboard } = useTranslation("dashboard");
  const [searchParams] = useSearchParams();

  // 扫码过来的参数，优先 mid，其次 memberId（兼容老二维码）
  const midParam =
    searchParams.get("mid") || searchParams.get("memberId");

  const [memberData, setMemberData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(
    function () {
      async function fetchMembership() {
        try {
          if (!midParam) {
            setError("missing_mid");
            setLoading(false);
            return;
          }

          // 1️⃣ 先按「文档 id = mid」查（门店 ST 会员就是这种结构）
          const refById = doc(db, "memberships", midParam);
          const snapById = await getDoc(refById);

          if (snapById.exists()) {
            setMemberData({
              id: snapById.id,
              ...snapById.data(),
            });
            setLoading(false);
            return;
          }

          // 2️⃣ 再按字段 memberId = mid 查（早期 Stripe RR 会员）
          const q = query(
            collection(db, "memberships"),
            where("memberId", "==", midParam)
          );
          const qsnap = await getDocs(q);

          if (!qsnap.empty) {
            const docSnap = qsnap.docs[0];
            setMemberData({
              id: docSnap.id,
              ...docSnap.data(),
            });
            setLoading(false);
            return;
          }

          // 两种方式都没查到
          setError("not_found");
          setLoading(false);
        } catch (e) {
          console.error("fetchMembership error", e);
          setError("error");
          setLoading(false);
        }
      }

      fetchMembership();
    },
    [midParam]
  );

  // 统一 loading 提示
  if (loading) {
    return (
      <div style={{ padding: "20px" }}>
        {tDashboard("loading") || "🔄 正在查询会员信息..."}
      </div>
    );
  }

  // 有错误代码时，给出更具体的提示
  if (error) {
    let msg = "";
    if (error === "missing_mid") {
      msg =
        tDashboard("verifyMissingMid") ||
        "❌ 二维码格式不正确（缺少会员编号）。";
    } else if (error === "not_found") {
      msg =
        tDashboard("errorNodata") ||
        "❌ 未找到有效会员，或会员已过期 / 已停用。";
    } else {
      msg =
        tDashboard("verifyError") ||
        "❌ 查询会员信息时发生错误，请稍后重试。";
    }

    return (
      <div style={{ padding: "20px", color: "red" }}>{msg}</div>
    );
  }

  // 理论上这里一定有数据，再兜底一次
  if (!memberData) {
    return (
      <div style={{ padding: "20px", color: "red" }}>
        {tDashboard("errorNodata") ||
          "❌ 暂未查询到会员数据。"}
      </div>
    );
  }

  const expiresDate = getExpiresDate(memberData.expiresAt);
  const statusText = getMembershipStatusText(
    memberData,
    tDashboard
  );

  // 会员编号：优先字段 memberId，没有就用文档 id
  const displayMemberId =
    memberData.memberId || memberData.id;

  // ✅ 这里的二维码只存「会员号本身」，和 Dashboard、门店打印一致
  const qrValue = displayMemberId;

  return (
    <div
      style={{
        padding: "20px",
        maxWidth: "500px",
        margin: "40px auto",
        textAlign: "center",
        background: "rgba(255,255,255,0.8)",
        borderRadius: "16px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        color: "#3c2e1d",
      }}
    >
      <h2
        style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          marginBottom: "16px",
        }}
      >
        {tDashboard("verifySuccessTitle") ||
          "✅ 会员验证成功"}
      </h2>

      <p style={{ margin: "8px 0", fontSize: "1rem" }}>
        <strong>
          {tDashboard("membershipMemberId") ||
            "会员编号："}
        </strong>{" "}
        {displayMemberId}
      </p>

      <p style={{ margin: "8px 0", fontSize: "1rem" }}>
        <strong>
          {tDashboard("membershipLevel") ||
            "会员类型："}
        </strong>{" "}
        {memberData.membershipType === "monthly"
          ? tDashboard("membershipMonthly") ||
            "月卡会员"
          : tDashboard("membershipYearly") ||
            "年卡会员"}
      </p>

      <p style={{ margin: "8px 0", fontSize: "1rem" }}>
        <strong>
          {tDashboard("membershipStatus") ||
            "当前状态："}
        </strong>{" "}
        {statusText}
      </p>

      <p
        style={{
          margin: "8px 0 20px",
          fontSize: "1rem",
        }}
      >
        <strong>
          {tDashboard("membershipValidity") ||
            "有效期至："}
        </strong>{" "}
        {expiresDate
          ? expiresDate.toLocaleDateString()
          : tDashboard("membershipUnknown") ||
            "暂无数据"}
      </p>

      <div style={{ marginTop: "20px" }}>
        <QRCode value={qrValue} size={150} />
      </div>
    </div>
  );
}
